import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, TextField, Button, Select, MenuItem,
  Avatar, Chip, ClickAwayListener,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { laborActions } from '../../store';
import { laborStatusColors, getAvatarColor, surfaces } from '../../theme/tokens';
import { parseTime, formatTime } from '../../utils/timeUtils';

const STATUS_CONFIG = {
  hold: { ...laborStatusColors.hold },
  pending: { ...laborStatusColors.pending },
  in_progress: { ...laborStatusColors.in_progress },
  complete: { ...laborStatusColors.complete },
};

const cellSx = { py: 0.5, px: 0.75, borderBottom: '1px solid', borderColor: 'divider' };
const headerSx = { fontWeight: 600, color: 'text.secondary', py: 0.75, px: 0.75, fontSize: 12 };

const EditableCell = ({ value, onChange, placeholder, type = 'text', inputProps, sx }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const parsed = type === 'number' ? (draft === '' ? null : parseFloat(draft)) : draft;
    if (parsed !== value) onChange(parsed);
  };

  if (!editing) {
    return (
      <Box
        onClick={() => setEditing(true)}
        sx={{
          minHeight: 28, display: 'flex', alignItems: 'center', cursor: 'text',
          px: 0.5, borderRadius: 0.5, fontSize: 12, color: value ? 'text.primary' : 'text.disabled',
          '&:hover': { backgroundColor: 'action.hover' },
          ...sx,
        }}
      >
        {value ?? placeholder ?? '—'}
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={commit}>
      <TextField
        inputRef={inputRef}
        size="small"
        variant="standard"
        type={type}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { commit(); e.preventDefault(); }
          if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
          if (e.key === 'Tab') commit();
        }}
        inputProps={{ style: { fontSize: 12, padding: '2px 4px' }, ...inputProps }}
        sx={{ minWidth: type === 'number' ? 60 : 100 }}
      />
    </ClickAwayListener>
  );
};

const UserCell = ({ userId, userName, members, onChange, readOnly }) => {
  const [open, setOpen] = useState(false);

  if (readOnly) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: getAvatarColor(userId) }}>
          {userName?.[0]?.toUpperCase() || '?'}
        </Avatar>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>{userName || 'Unknown'}</Typography>
      </Box>
    );
  }

  return (
    <Select
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={userId || ''}
      onChange={(e) => { onChange(e.target.value); setOpen(false); }}
      size="small"
      variant="standard"
      disableUnderline
      sx={{ fontSize: 12 }}
      renderValue={(val) => {
        const member = members.find((m) => m._id === val);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: getAvatarColor(val) }}>
              {member?.name?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>{member?.name || 'Select...'}</Typography>
          </Box>
        );
      }}
    >
      {members.map((m) => (
        <MenuItem key={m._id} value={m._id} sx={{ fontSize: 12 }}>{m.name}</MenuItem>
      ))}
    </Select>
  );
};

const StatusCell = ({ value, onChange }) => (
  <Select
    value={value || 'pending'}
    onChange={(e) => onChange(e.target.value)}
    size="small"
    variant="standard"
    disableUnderline
    sx={{ fontSize: 12 }}
    renderValue={(val) => (
      <Chip
        label={STATUS_CONFIG[val]?.label || val}
        size="small"
        sx={{
          height: 20, fontSize: 10, fontWeight: 600, cursor: 'pointer',
          backgroundColor: STATUS_CONFIG[val]?.bg, color: STATUS_CONFIG[val]?.color,
        }}
      />
    )}
  >
    <MenuItem value="hold" sx={{ fontSize: 12 }}>Hold</MenuItem>
    <MenuItem value="pending" sx={{ fontSize: 12 }}>Pending</MenuItem>
    <MenuItem value="in_progress" sx={{ fontSize: 12 }}>In Progress</MenuItem>
    <MenuItem value="complete" sx={{ fontSize: 12 }}>Complete</MenuItem>
  </Select>
);

const AddRow = ({ members, defaultUserId, hasActiveEntries, onAdd }) => {
  const emptyForm = {
    user: defaultUserId || '',
    status: hasActiveEntries ? 'hold' : 'pending',
    time: '',
    note: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [active, setActive] = useState(false);

  useEffect(() => {
    setForm((prev) => ({ ...prev, user: defaultUserId || '' }));
  }, [defaultUserId]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      status: hasActiveEntries ? 'hold' : 'pending',
    }));
  }, [hasActiveEntries]);

  const submit = () => {
    if (!form.user) return;
    const payload = { user: form.user, status: form.status };
    if (form.time) payload.time = parseTime(form.time);
    if (form.note) payload.note = form.note;
    onAdd(payload);
    setForm({ ...emptyForm, user: defaultUserId || '', status: hasActiveEntries ? 'hold' : 'pending' });
    setActive(false);
  };

  const cancel = () => {
    setForm({ ...emptyForm, user: defaultUserId || '' });
    setActive(false);
  };

  if (!active) {
    return (
      <TableRow
        onClick={() => setActive(true)}
        sx={{
          cursor: 'pointer',
          '& td': { py: 0.75, borderBottom: 'none' },
          '&:hover td': { backgroundColor: 'action.hover' },
        }}
      >
        <TableCell colSpan={7} sx={{ color: 'text.disabled', fontSize: 12, pl: 5 }}>
          + Click to add work item...
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow sx={{ '& td': { ...cellSx, backgroundColor: surfaces.subtle } }}>
      <TableCell sx={{ width: 30, px: 0.5 }} />
      <TableCell>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, fontSize: 11 }}>
          —
        </Typography>
      </TableCell>
      <TableCell>
        <UserCell userId={form.user} members={members} onChange={(val) => setForm({ ...form, user: val })} />
      </TableCell>
      <TableCell>
        <StatusCell value={form.status} onChange={(val) => setForm({ ...form, status: val })} />
      </TableCell>
      <TableCell>
        <TextField
          size="small" variant="standard" value={form.time} placeholder="e.g. 1h 30m"
          onChange={(e) => setForm({ ...form, time: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') cancel();
          }}
          inputProps={{ min: 0, step: 0.25, style: { fontSize: 12, width: 50, padding: '2px 4px' } }}
        />
      </TableCell>
      <TableCell>
        <TextField
          size="small" variant="standard" value={form.note} placeholder="Add note..."
          autoFocus
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            if (e.key === 'Escape') cancel();
          }}
          inputProps={{ style: { fontSize: 12, padding: '2px 4px' } }}
          sx={{ minWidth: 100 }}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="text"
            onClick={submit}
            disabled={!form.user}
            sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: 11, fontWeight: 600, textTransform: 'none' }}
          >
            Save
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={cancel}
            sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: 11, fontWeight: 500, textTransform: 'none', color: 'text.disabled' }}
          >
            Esc
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export const LaborSection = ({ taskId, projectMembers }) => {
  const dispatch = useDispatch();
  const laborEntries = useSelector((state) => state.labor.laborEntries);
  const myProfile = useSelector((state) => state.auth.myProfile);
  const allUsers = useSelector((state) => state.users.users);

  const members = projectMembers?.length ? projectMembers : (allUsers || []);
  const entries = laborEntries || [];

  useEffect(() => {
    if (taskId) dispatch(laborActions.getLabor(taskId));
  }, [dispatch, taskId]);

  const hasActiveEntries = entries.some(
    (e) => e.status === 'pending' || e.status === 'in_progress',
  );

  const handleAdd = useCallback((payload) => {
    dispatch(laborActions.createLabor({ ...payload, task: taskId }));
  }, [dispatch, taskId]);

  const handleFieldChange = useCallback((id, field, value) => {
    dispatch(laborActions.updateLabor(id, { [field]: value }));
  }, [dispatch]);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const srcIdx = result.source.index;
    const destIdx = result.destination.index;
    if (srcIdx === destIdx) return;

    const reordered = Array.from(entries);
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(destIdx, 0, moved);

    const items = reordered.map((entry, idx) => ({ id: entry._id, order: idx }));
    dispatch(laborActions.reorderLabor(items));
  }, [dispatch, entries]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1 }}>
        Work Items ({entries.length})
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: surfaces.elevated }}>
                <TableCell sx={{ ...headerSx, width: 30 }} />
                <TableCell sx={headerSx}>#</TableCell>
                <TableCell sx={headerSx}>User</TableCell>
                <TableCell sx={headerSx}>Status</TableCell>
                <TableCell sx={headerSx}>Time</TableCell>
                <TableCell sx={headerSx}>Note</TableCell>
                <TableCell sx={{ ...headerSx, width: 50 }} align="right" />
              </TableRow>
            </TableHead>
            <Droppable droppableId="labor-list">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {entries.map((entry, index) => (
                    <Draggable key={entry._id} draggableId={entry._id} index={index}>
                      {(provided, snapshot) => (
                        <TableRow
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            '& td': cellSx,
                            backgroundColor: snapshot.isDragging ? surfaces.subtle : 'inherit',
                            ...(entry.status === 'hold' && { opacity: 0.6 }),
                          }}
                        >
                          <TableCell sx={{ width: 30, px: 0.5 }}>
                            <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab' }}>
                              <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, fontSize: 11 }}>
                              #{index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <UserCell
                              userId={entry.user?._id}
                              userName={entry.user?.name}
                              members={members}
                              onChange={(val) => handleFieldChange(entry._id, 'user', val)}
                            />
                          </TableCell>
                          <TableCell>
                            <StatusCell value={entry.status} onChange={(val) => handleFieldChange(entry._id, 'status', val)} />
                          </TableCell>
                          <TableCell>
                            <EditableCell
                              value={entry.time ? formatTime(entry.time) : ''}
                              placeholder="e.g. 1h 30m"
                              onChange={(val) => {
                                const minutes = parseTime(val);
                                if (minutes !== null) handleFieldChange(entry._id, 'time', minutes);
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <EditableCell
                              value={entry.note}
                              placeholder="Add note..."
                              onChange={(val) => handleFieldChange(entry._id, 'note', val)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => dispatch(laborActions.deleteLabor(entry._id))}
                              sx={{ color: 'error.main', p: 0.25, opacity: 0.4, '&:hover': { opacity: 1 } }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          <TableBody>
              <AddRow
                members={members}
                defaultUserId={myProfile?._id}
                hasActiveEntries={hasActiveEntries}
                onAdd={handleAdd}
              />
            </TableBody>
          </Table>
        </TableContainer>
      </DragDropContext>
    </Box>
  );
};
